import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  EnhancedSeoHead,
  FAQ,
  combineSchemas,
  generateBreadcrumbSchema,
  generateFAQSchema,
} from "@appcaire/shared/seo";
import { ArrowRight, Star } from "lucide-react";

const FAQ_ITEMS = [
  {
    question: "Var hittar jag recensioner av hemtjänst i Nacka?",
    answer:
      "Officiella omdömen kommer främst från Socialstyrelsens brukarundersökning och kommunens öppna data. På vår anordnarlista kan du jämföra kvalitetsindikatorer sida vid sida.",
  },
  {
    question: "Är privata Google-recensioner samma sak som officiella betyg?",
    answer:
      "Nej. Brukarundersökningar mäter flera hemtjänstmått på samma sätt för alla utförare. Privata recensioner kan vara användbara som komplement men är inte jämförbara på samma sätt.",
  },
  {
    question: "Hur använder jag omdömen när jag väljer utförare?",
    answer:
      "Titta på kontinuitet, nöjdhet och hur utföraren kommunicerar. Jämför sedan flera anordnare i Nacka och ställ frågor innan du väljer.",
  },
];

/**
 * Recensioner — SEO landing for “nacka hemtjänst recensioner”.
 * Points users to the compare list; does not invent review scores.
 */
const Recensioner: React.FC = () => {
  const schemaMarkup = useMemo(
    () =>
      combineSchemas([
        generateBreadcrumbSchema([
          { name: "Hem", url: "https://nackahemtjanst.se/" },
          {
            name: "Recensioner",
            url: "https://nackahemtjanst.se/recensioner",
          },
        ]),
        generateFAQSchema(
          FAQ_ITEMS.map((item) => ({
            question: item.question,
            answer: item.answer,
          })),
          "https://nackahemtjanst.se/recensioner",
        ),
      ]),
    [],
  );

  return (
    <>
      <EnhancedSeoHead
        title="Nacka hemtjänst recensioner – jämför omdömen | Nacka Hemtjänst"
        description="Så tolkar du recensioner och brukaromdömen för hemtjänst i Nacka. Jämför anordnare med officiella kvalitetsindikatorer – inte bara anekdoter."
        canonicalUrl="https://nackahemtjanst.se/recensioner"
        keywords={[
          "nacka hemtjänst recensioner",
          "hemtjänst nacka omdömen",
          "brukarundersökning hemtjänst nacka",
          "jämför hemtjänst nacka",
        ]}
        contentClassification="Consumer Portal"
        targetAudience="seniors, families"
        coverage="Sweden"
        schemaMarkup={schemaMarkup ?? undefined}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      <section className="relative bg-gradient-to-b from-emerald-50 via-white to-white px-4 py-16 md:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-6 inline-block rounded-2xl border border-emerald-100 bg-white/80 p-6 shadow-lg backdrop-blur">
            <Star className="h-12 w-12 text-amber-500" />
          </div>
          <h1 className="mb-6 text-4xl font-bold tracking-tight text-surface-900 md:text-5xl">
            Hemtjänst recensioner i Nacka
          </h1>
          <p className="mb-8 text-xl leading-relaxed text-surface-600">
            Vi samlar inte anonyma betygsstjärnor. I stället hjälper vi dig läsa
            officiella omdömen och jämföra utförare i Nacka med samma mått.
          </p>
          <Link
            to="/utforare"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-8 py-4 text-lg font-semibold text-white shadow-lg transition-all hover:bg-emerald-700 active:scale-[0.98]"
          >
            Jämför anordnare
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      <section className="bg-white px-4 py-16">
        <div className="mx-auto max-w-3xl space-y-6 text-lg leading-relaxed text-surface-700">
          <p>
            När du söker efter recensioner av hemtjänst i Nacka är det lätt att
            fastna i enstaka omdömen. Officiella brukarundersökningar ger en mer
            rättvis bild eftersom alla utförare mäts på samma frågor.
          </p>
          <p>
            På anordnarlistan kan du se kvalitetsindikatorer och gå vidare till
            varje utförare. Använd också vår guide för att välja hemtjänst innan
            du bestämmer dig.
          </p>
          <div className="flex flex-wrap gap-4 pt-2">
            <Link
              to="/valja"
              className="font-semibold text-emerald-700 underline-offset-4 hover:underline"
            >
              Guide: välja hemtjänst
            </Link>
            <Link
              to="/utforare"
              className="font-semibold text-emerald-700 underline-offset-4 hover:underline"
            >
              Lista över utförare
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 pb-16">
        <div className="mx-auto max-w-3xl space-y-6 text-lg leading-relaxed text-surface-700">
          <h2 className="text-2xl font-bold text-surface-900">
            Vad mäter brukarundersökningen?
          </h2>
          <p>
            Socialstyrelsen skickar varje år enkäten ”Vad tycker de äldre om
            äldreomsorgen?” till personer över 65 år som har hemtjänst. Frågorna
            handlar om sådant du själv märker i vardagen:
          </p>
          <ul className="list-disc space-y-2 pl-6">
            <li>hur nöjd du är med hemtjänsten som helhet</li>
            <li>om personalen bemöter dig bra och har tillräckligt med tid</li>
            <li>om du känner dig trygg med hjälpen hemma</li>
            <li>om du kan påverka när och hur du får hjälp</li>
            <li>om du får veta i förväg när något ändras</li>
          </ul>
          <p>
            Resultaten redovisas per kommun och ofta per utförare. Hos en liten
            utförare räcker några få svar för att flytta resultatet, så jämför
            gärna flera år innan du drar slutsatser.
          </p>
          <h2 className="text-2xl font-bold text-surface-900">
            Om du inte är nöjd
          </h2>
          <p>
            Prata först med utföraren. Ändras inget kan du kontakta din
            biståndshandläggare. I Nacka kan du byta utförare utan att ange
            skäl. Så går det till står i{" "}
            <Link
              to="/byta"
              className="font-semibold text-emerald-700 underline-offset-4 hover:underline"
            >
              guiden om att byta utförare
            </Link>
            , och vad du har rätt till står på sidan om{" "}
            <Link
              to="/rattigheter"
              className="font-semibold text-emerald-700 underline-offset-4 hover:underline"
            >
              dina rättigheter
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="bg-surface-50 px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <FAQ items={FAQ_ITEMS} title="Vanliga frågor om recensioner" />
        </div>
      </section>
    </>
  );
};

export default Recensioner;
